import { Button } from '@runtime/components/ui/Button'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useAppStore } from '@runtime/core/store/appStore'
import pageStyles from '@studio/routes/hub/admin/HubAdminRoute.module.scss'
import HubPageShell from '@studio/routes/hub/components/HubPageShell'
import logoUnitedSquareTileSrc from '@studio/routes/hub/admin/assets/laurie/logo-united-square-tuile-centrale.png'

import styles from './StudioAccessDeniedPage.module.scss'

type StudioAccessDeniedPageProps = {
  mode?: 'login' | 'insufficient-permissions'
  accountLabel?: string | null
}

export default function StudioAccessDeniedPage({
  mode = 'login',
  accountLabel = null,
}: StudioAccessDeniedPageProps) {
  const { t } = useI18n()
  const setLoginOpen = useAppStore(state => state.setLoginOpen)
  const hasInsufficientPermissions = mode === 'insufficient-permissions'
  const highlightedTools = [
    {
      title: t('hub.admin.qrTool.title'),
      description: t('hub.admin.qrTool.menuDesc'),
    },
    {
      title: t('hub.admin.generator.title'),
      description: t('hub.admin.generator.menuDesc'),
    },
    {
      title: t('hub.admin.privateBoards.title'),
      description: t('hub.admin.privateBoards.menuDesc'),
    },
  ]

  return (
    <HubPageShell
      showProfileButton={false}
      showHomeButton={false}
      hero={
        <div className={pageStyles.hero}>
          <div className={pageStyles.heroTitle}>{t('hub.admin.title')}</div>
          <p className={pageStyles.heroDesc}>
            {t(
              hasInsufficientPermissions
                ? 'hub.admin.login.accessDeniedMobileIntro'
                : 'hub.admin.login.mobileIntro',
            )}
          </p>
        </div>
      }
    >
      <section className={styles.entry}>
        <div className={styles.entryGrid}>
          <article className={styles.brandPanel}>
            <div className={styles.brandBadge}>{t('hub.admin.title')}</div>

            <div className={styles.brandLockup}>
              <div className={styles.brandLogoFrame} aria-hidden="true">
                <img src={logoUnitedSquareTileSrc} alt="" className={styles.brandLogo} />
              </div>

              <div className={styles.brandCopy}>
                <h1 className={styles.brandTitle}>{t('hub.admin.login.title')}</h1>
                <p className={styles.brandDescription}>{t('hub.admin.login.description')}</p>
              </div>
            </div>

            <div className={styles.featureGrid}>
              {highlightedTools.map(tool => (
                <section key={tool.title} className={styles.featureCard}>
                  <h2 className={styles.featureTitle}>{tool.title}</h2>
                  <p className={styles.featureDescription}>{tool.description}</p>
                </section>
              ))}
            </div>
          </article>

          <aside className={styles.loginPanel}>
            <div
              className={`${styles.loginCard} ${
                hasInsufficientPermissions ? styles.accessDeniedCard : ''
              }`}
            >
              <div className={styles.loginEyebrow}>
                {t(
                  hasInsufficientPermissions
                    ? 'hub.admin.login.accessDeniedEyebrow'
                    : 'hub.admin.login.cardEyebrow',
                )}
              </div>
              <div role={hasInsufficientPermissions ? 'alert' : undefined}>
                <h2 className={styles.loginTitle}>
                  {t(
                    hasInsufficientPermissions
                      ? 'hub.admin.login.accessDeniedTitle'
                      : 'hub.admin.login.cardTitle',
                  )}
                </h2>
                <p className={styles.loginDescription}>
                  {t(
                    hasInsufficientPermissions
                      ? 'hub.admin.login.accessDeniedDescription'
                      : 'hub.admin.login.cardDescription',
                  )}
                </p>
                {hasInsufficientPermissions && accountLabel ? (
                  <p className={styles.accessDeniedAccount}>
                    {t('hub.admin.login.accessDeniedAccount', { account: accountLabel })}
                  </p>
                ) : null}
              </div>

              <Button
                className={styles.loginButton}
                data-testid="studio-login-button"
                onClick={() => setLoginOpen(true)}
              >
                {t(
                  hasInsufficientPermissions
                    ? 'hub.admin.login.accessDeniedCta'
                    : 'profile.loginButton.labelFallback',
                )}
              </Button>

              <p className={styles.loginHint}>
                {t(
                  hasInsufficientPermissions
                    ? 'hub.admin.login.accessDeniedHint'
                    : 'hub.admin.login.cardHint',
                )}
              </p>
            </div>
          </aside>
        </div>
      </section>
    </HubPageShell>
  )
}
